import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { broadcast, onSessionMessage, SESSION_MSG } from '../lib/sessionChannel';

// Un solo design system per tutto il prodotto; la preferenza riguarda
// esclusivamente la modalità cromatica: Chiaro, Scuro o Sistema
// (che segue in tempo reale `prefers-color-scheme: dark` del sistema operativo).

export const MODES = [
  { key: 'light',  label: 'Chiaro',  hint: 'Superfici porcellana antiriflesso su carta ardesia per turni diurni' },
  { key: 'dark',   label: 'Scuro',   hint: 'Superfici ardesia calibrata anti-aloni per ridurre l\'affaticamento visivo' },
  { key: 'system', label: 'Sistema', hint: 'Segue automaticamente l\'impostazione del dispositivo' },
];

const isMode = (m) => MODES.some((x) => x.key === m);

function systemPrefersDark() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveMode(mode) {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  return systemPrefersDark() ? 'dark' : 'light';
}

function apply(mode) {
  if (typeof document === 'undefined') return 'light';
  const resolved = resolveMode(mode);
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.removeAttribute('data-theme');
  root.style.colorScheme = resolved;
  return resolved;
}

export const useThemeStore = create(
  persist(
    (set, get) => ({
      mode: 'light',
      resolvedMode: 'light',

      toggle: () => {
        const currentResolved = get().resolvedMode || resolveMode(get().mode);
        const next = currentResolved === 'light' ? 'dark' : 'light';
        const resolvedMode = apply(next);
        set({ mode: next, resolvedMode });
        broadcast(SESSION_MSG.theme, { mode: next });
      },

      setMode: (mode) => {
        const next = isMode(mode) ? mode : 'light';
        const resolvedMode = apply(next);
        if (next === get().mode && resolvedMode === get().resolvedMode) return;
        set({ mode: next, resolvedMode });
        broadcast(SESSION_MSG.theme, { mode: next });
      },

      init: () => {
        const current = isMode(get().mode) ? get().mode : 'light';
        const resolvedMode = apply(current);
        set({ mode: current, resolvedMode });
      },

      _adopt: ({ mode }) => {
        if (!isMode(mode)) return;
        const resolvedMode = apply(mode);
        set({ mode, resolvedMode });
      },
    }),
    {
      name: 'wfm-theme',
      partialize: (state) => ({ mode: state.mode }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          const validMode = isMode(state.mode) ? state.mode : 'light';
          state.mode = validMode;
          state.resolvedMode = apply(validMode);
        }
      },
    }
  )
);

if (typeof window !== 'undefined') {
  onSessionMessage(SESSION_MSG.theme, (payload) => {
    useThemeStore.getState()._adopt(payload || {});
  });

  if (typeof window.matchMedia === 'function') {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onSystemChange = () => {
      const state = useThemeStore.getState();
      if (state.mode === 'system') {
        const resolvedMode = apply('system');
        useThemeStore.setState({ resolvedMode });
      }
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onSystemChange);
    }
  }
}
